import React, { useState, useEffect } from 'react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import Button from '../Components/Button';
import CheckItem from '../Components/CheckItem';
import { Check } from 'lucide-react';

// Import config files
import signupPageContent from '../config/app-context/signup-page-content.json';
import signupFormConfig from '../config/app-context/signup-form-config.json';

const SignupPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    healthGoals: '',
    dietaryRestrictions: '',
    hearAbout: '',
    agreeTerms: false
  });
  
  const [formState, setFormState] = useState({
    submitting: false,
    submitted: false,
    error: null
  });
  const API_URL = process.env.REACT_APP_API_URL

  useEffect(() => {
    // In a production app, you might fetch these configs from an API
    // For now, we'll just use the imported JSON files
    setContent({
      page: signupPageContent,
      form: signupFormConfig,
    });
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState({ ...formState, submitting: true, error: null });

    try {
      // Use API endpoint from config
      const response = await fetch(`${API_URL}/beta-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      setFormState({
        submitting: false,
        submitted: true,
        error: null
      });
    } catch (error) {
      console.error('Submission error:', error);
      setFormState({
        submitting: false,
        submitted: false,
        error: content.page.errorMessage
      });
    }
  };

  // Show loading state if content isn't loaded yet
  if (loading || !content) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  // Destructure content for easier access
  const { page, form } = content;

  if (formState.submitted) {
    const { successMessage } = page;
    
    return (
      <div className="font-sans text-gray-800">
        <Header isMainPage={false} />
        
        <main className="py-12">
          <div className="container mx-auto px-6">
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-4xl font-bold mb-4">{successMessage.title}</h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
                {successMessage.description}
              </p>
              <Button href="/" variant="primary" size="large">
                {successMessage.buttonText}
              </Button>
            </div>
          </div>
        </main>
        
        <Footer simplified={true} />
      </div>
    );
  }

  return (
    <div className="font-sans text-gray-800">
      <Header isMainPage={false} />

      <main className="py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-4xl font-bold mb-4">{page.pageTitle}</h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">{page.pageDescription}</p>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {/* Benefits */}
              <div className="md:col-span-2 bg-gray-50 p-6 rounded-lg">
                <h2 className="text-2xl font-semibold mb-6">{page.benefits.title}</h2>
                <ul className="space-y-4">
                  {page.benefits.items.map((benefit, index) => (
                    <CheckItem key={index} bgColor="bg-blue-100" textColor="text-blue-600" iconSize="h-4 w-4">
                      <span>{benefit}</span>
                    </CheckItem>
                  ))}
                </ul>
                <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <h3 className="font-semibold text-blue-800 mb-2">{page.benefits.limitedAvailability.title}</h3>
                  <p className="text-blue-700 text-sm">{page.benefits.limitedAvailability.description}</p>
                </div>
              </div>

              {/* Form */}
              <div className="md:col-span-3 bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold mb-6">{page.formSection.title}</h2>
                <form onSubmit={handleSubmit}>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {form.personal.slice(0, 2).map((field) => (
                      <div key={field.id}>
                        <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label} {field.required && '*'}
                        </label>
                        <input
                          type={field.type}
                          id={field.id}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {form.personal.slice(2).map((field) => (
                      <div key={field.id}>
                        <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label} {field.required && '*'}
                        </label>
                        <input
                          type={field.type}
                          id={field.id}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    ))}
                  </div>

                  {form.preferences.map((field) => (
                    <div key={field.id} className="mb-6">
                      <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label} {field.required && '*'}
                      </label>
                      
                      {field.type === 'textarea' ? (
                        <textarea
                          id={field.id}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          rows="3"
                          required={field.required}
                          placeholder={field.placeholder}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        ></textarea>
                      ) : field.type === 'select' ? (
                        <select
                          id={field.id}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {field.options.map((option, idx) => (
                            <option key={idx} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                  ))}

                  <div className="mb-6">
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id={form.terms.id}
                          name={form.terms.name}
                          type={form.terms.type}
                          checked={formData[form.terms.name]}
                          onChange={handleChange}
                          required={form.terms.required}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor={form.terms.id} className="font-medium text-gray-700">
                          I agree to the{' '}
                          <a href={form.terms.links.terms.url} className="text-blue-600 hover:underline">
                            {form.terms.links.terms.text}
                          </a>{' '}
                          and{' '}
                          <a href={form.terms.links.privacy.url} className="text-blue-600 hover:underline">
                            {form.terms.links.privacy.text}
                          </a> *
                        </label>
                      </div>
                    </div>
                  </div>

                  {formState.error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg text-red-700">
                      {formState.error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    loading={formState.submitting}
                    disabled={formState.submitting}
                    fullWidth
                  >
                    {page.formSection.submitButton}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer simplified={true} />
    </div>
  );
};

export default SignupPage;